import React from 'react';
import { Chip, ChipProps, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getStatusConfig, getStatusColor, getStatusLabel, getStatusDescription } from '../../utils/reportPortalToolkit';

interface StatusChipProps extends Omit<ChipProps, 'label'> {
  status: string;
  showTooltip?: boolean;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined';
  showShortLabel?: boolean;
}

const StatusChip: React.FC<StatusChipProps> = ({
  status,
  showTooltip = true,
  size = 'small',
  variant = 'filled',
  showShortLabel = false,
  ...chipProps
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const themeMode = isDarkMode ? 'dark' : 'light';
  
  const statusConfig = getStatusConfig(status);
  const bgColor = getStatusColor(status, 'bg', themeMode);
  const textColor = getStatusColor(status, 'text', themeMode);
  const borderColor = getStatusColor(status, 'border', themeMode);
  const label = showShortLabel && statusConfig?.shortLabel ? statusConfig.shortLabel : getStatusLabel(status);
  const description = getStatusDescription(status);

  // Custom styling based on theme and status
  const chipStyle = {
    backgroundColor: variant === 'filled' ? bgColor : 'transparent',
    color: variant === 'filled' ? textColor : textColor,
    border: variant === 'outlined' ? `1px solid ${borderColor}` : 'none',
    fontWeight: 600,
    fontSize: size === 'small' ? '0.7rem' : '0.8rem',
    textTransform: 'capitalize' as const,
    letterSpacing: '0.3px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: variant === 'filled' ? bgColor : bgColor,
      opacity: 0.9,
      transform: 'translateY(-1px)',
      boxShadow: `0 2px 8px ${borderColor}40`
    }
  };

  const chip = (
    <Chip
      label={label}
      size={size}
      variant={variant}
      sx={chipStyle}
      {...chipProps}
    />
  );

  if (showTooltip && description) {
    return (
      <Tooltip 
        title={description}
        arrow
        placement="top"
      >
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

export default StatusChip;
